import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";

import { makeStyles } from "@material-ui/core/styles";
import { green, red } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";

// Mínimo de dígitos antes de disparar a checagem ao vivo — evita bater na
// API a cada tecla enquanto o usuário ainda está digitando o DDD/começo do
// número (ex: "55", "5599"). 8 dígitos é o mínimo aceito pelo ContactSchema.
const MIN_DIGITS_TO_CHECK = 8;

// Mínimo de caracteres pra disparar a busca de contatos já cadastrados —
// evita bater na API a cada tecla nos primeiros caracteres do nome/número.
const MIN_CHARS_TO_SEARCH = 2;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  nameNumberRow: {
    display: "flex",
    alignItems: "flex-start",
  },

  nameField: {
    width: "50%",
    marginRight: theme.spacing(1),
  },

  numberField: {
    width: "50%",
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  numberStatus: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    marginLeft: 14,
    fontSize: 12,
  },

  numberStatusValid: {
    color: green[600],
  },

  numberStatusInvalid: {
    color: red[600],
  },

  numberStatusChecking: {
    color: theme.palette.text.secondary,
  },

  warningButton: {
    backgroundColor: "#ed6c02",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#c25a00",
    },
  },

  invalidNumberWarning: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginRight: "auto",
    maxWidth: 380,
    fontSize: 12,
    color: (theme.palette.warning && theme.palette.warning.dark) || "#a35a00",
  },
}));

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email"),
});

/**
 * Prévia de "é WhatsApp?" enquanto o usuário digita — sem isto, o item só
 * descobria isWhatsappValid=false depois de salvar (ou nunca, já que a
 * campanha simplesmente ignora contatos inválidos sem avisar ninguém, #4071).
 * Debounce + mínimo de dígitos evita bater na API a cada tecla. Elevado a
 * hook (em vez de ficar preso no componente visual) porque o botão
 * "Adicionar" também precisa saber o status pra mostrar o aviso de risco.
 */
const useNumberValidation = (number) => {
  const [debouncedNumber] = useDebounce(number, 600);
  const [status, setStatus] = useState(null); // null | "checking" | "valid" | "invalid"

  useEffect(() => {
    const digits = String(debouncedNumber || "").replace(/\D/g, "");
    if (digits.length < MIN_DIGITS_TO_CHECK) {
      setStatus(null);
      return;
    }

    let cancelled = false;
    setStatus("checking");

    (async () => {
      try {
        const { data } = await api.get("/contact-list-items/check-number", {
          params: { number: digits },
        });
        if (!cancelled) {
          setStatus(data.valid ? "valid" : "invalid");
        }
      } catch (err) {
        if (!cancelled) {
          setStatus(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedNumber]);

  return status;
};

const NumberValidationStatus = ({ status, classes }) => {
  if (!status) return null;

  if (status === "checking") {
    return (
      <div className={`${classes.numberStatus} ${classes.numberStatusChecking}`}>
        <CircularProgress size={12} />
        <span>{i18n.t("contactModal.form.numberChecking")}</span>
      </div>
    );
  }

  if (status === "valid") {
    return (
      <div className={`${classes.numberStatus} ${classes.numberStatusValid}`}>
        <CheckCircleIcon style={{ fontSize: 14 }} />
        <span>{i18n.t("contactModal.form.numberValid")}</span>
      </div>
    );
  }

  return (
    <div className={`${classes.numberStatus} ${classes.numberStatusInvalid}`}>
      <ErrorIcon style={{ fontSize: 14 }} />
      <span>{i18n.t("contactModal.form.numberInvalid")}</span>
    </div>
  );
};

/**
 * Aviso de risco ao lado do botão "Adicionar" quando o número não valida —
 * pedido explícito: NÃO bloquear o envio (o usuário pode ter um motivo real,
 * ou a validação pode ter falhado por instabilidade), só deixar claro que
 * insistir em mandar mensagem pra número inexistente pode gerar punição da
 * própria Meta/WhatsApp (banimento/limitação da conexão).
 */
const InvalidNumberWarning = ({ classes }) => (
  <div className={classes.invalidNumberWarning}>
    <WarningIcon style={{ fontSize: 16 }} />
    <span>{i18n.t("contactModal.form.numberInvalidWarning")}</span>
  </div>
);

/**
 * Confirmação explícita antes de salvar um número não validado — pedido
 * explícito: precisa de um botão com o texto "Usar mesmo assim" (não o
 * genérico "OK" do ConfirmationModal compartilhado), então é um modal
 * próprio em vez de reaproveitar o componente genérico.
 */
const InvalidNumberConfirmModal = ({ open, onCancel, onConfirm }) => (
  <Dialog open={open} onClose={onCancel} aria-labelledby="invalid-number-confirm-dialog">
    <DialogTitle id="invalid-number-confirm-dialog">
      {i18n.t("contactModal.form.numberInvalidConfirmTitle")}
    </DialogTitle>
    <DialogContent dividers>
      <Typography>{i18n.t("contactModal.form.numberInvalidWarning")}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} color="secondary" variant="outlined">
        {i18n.t("contactModal.buttons.cancel")}
      </Button>
      <Button onClick={onConfirm} variant="contained" color="primary">
        {i18n.t("contactModal.buttons.useAnyway")}
      </Button>
    </DialogActions>
  </Dialog>
);

/**
 * Campo único de Nome que também sugere contatos JÁ cadastrados (tabela
 * Contact — já teve ticket/mensagem real). Digitar texto livre funciona
 * normalmente (freeSolo — vira o nome de um contato novo); se casar com um
 * contato existente, aparece como opção selecionável e, ao escolher,
 * preenche número/email automaticamente. Sem isto, o usuário sempre digitava
 * do zero mesmo quando o contato já existe no sistema, arriscando erro de
 * digitação e perdendo o número já validado pelo pipeline de mensagens. Só
 * aparece ao ADICIONAR (não ao editar um item já salvo da lista).
 */
const NameWithExistingContact = ({
  classes,
  value,
  error,
  helperText,
  onTextChange,
  onSelectContact,
}) => {
  const [debouncedSearchParam] = useDebounce(value, 400);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (String(debouncedSearchParam || "").length < MIN_CHARS_TO_SEARCH) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const { data } = await api.get("/contacts/list", {
          params: { name: debouncedSearchParam },
        });
        if (!cancelled) {
          setOptions(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchParam]);

  return (
    <Autocomplete
      freeSolo
      className={classes.nameField}
      options={options}
      loading={loading}
      inputValue={value || ""}
      filterOptions={(x) => x} // a filtragem já é feita no backend (busca por nome/número)
      getOptionLabel={(option) =>
        typeof option === "string" ? option : `${option.name} — ${option.number}`
      }
      onInputChange={(e, newValue, reason) => {
        if (reason === "input") onTextChange(newValue);
      }}
      onChange={(e, selected) => {
        if (selected && typeof selected !== "string") onSelectContact(selected);
      }}
      noOptionsText={i18n.t("contactModal.form.existingContactNoOptions")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={i18n.t("contactModal.form.name")}
          autoFocus
          fullWidth
          error={error}
          helperText={helperText}
          variant="outlined"
          margin="dense"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

/**
 * Corpo do formulário extraído do render-prop do Formik — precisa ser um
 * componente próprio (não uma função inline dentro do render-prop) para que
 * useNumberValidation possa ser chamado como hook legítimo (regra dos hooks:
 * só em componente de função ou custom hook, nunca dentro de um callback).
 */
const ContactFormFields = ({
  classes,
  contactId,
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  submitForm,
  handleClose,
}) => {
  const numberStatus = useNumberValidation(values.number);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Form>
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>
          {i18n.t("contactModal.form.mainInfo")}
        </Typography>
        <div className={classes.nameNumberRow}>
          {contactId ? (
            <Field
              as={TextField}
              label={i18n.t("contactModal.form.name")}
              name="name"
              autoFocus
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              variant="outlined"
              margin="dense"
              className={classes.nameField}
            />
          ) : (
            <NameWithExistingContact
              classes={classes}
              value={values.name}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              onTextChange={(text) => setFieldValue("name", text)}
              onSelectContact={(selected) => {
                setFieldValue("name", selected.name || "");
                setFieldValue("number", selected.number || "");
                setFieldValue("email", selected.email || "");
              }}
            />
          )}
          <Field
            as={TextField}
            label={i18n.t("contactModal.form.number")}
            name="number"
            error={touched.number && Boolean(errors.number)}
            helperText={touched.number && errors.number}
            placeholder="5513912344321"
            variant="outlined"
            margin="dense"
            className={classes.numberField}
          />
        </div>
        <NumberValidationStatus status={numberStatus} classes={classes} />
        <div>
          <Field
            as={TextField}
            label={i18n.t("contactModal.form.email")}
            name="email"
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
            placeholder="Email address"
            fullWidth
            margin="dense"
            variant="outlined"
          />
        </div>
      </DialogContent>
      <DialogActions>
        {numberStatus === "invalid" && (
          <InvalidNumberWarning classes={classes} />
        )}
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={isSubmitting}
          variant="outlined"
        >
          {i18n.t("contactModal.buttons.cancel")}
        </Button>
        <Button
          type="button"
          color="primary"
          disabled={isSubmitting}
          variant="contained"
          className={`${classes.btnWrapper} ${numberStatus === "invalid" ? classes.warningButton : ""}`}
          onClick={() => {
            if (numberStatus === "invalid") {
              setConfirmOpen(true);
            } else {
              submitForm();
            }
          }}
        >
          {numberStatus === "invalid" && (
            <WarningIcon style={{ fontSize: 18, marginRight: 6 }} />
          )}
          {contactId
            ? `${i18n.t("contactModal.buttons.okEdit")}`
            : `${i18n.t("contactModal.buttons.okAdd")}`}
          {isSubmitting && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Button>
      </DialogActions>
      <InvalidNumberConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          submitForm();
        }}
      />
    </Form>
  );
};

const ContactListItemModal = ({
  open,
  onClose,
  contactId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const {
    user: { companyId },
  } = useContext(AuthContext);
  const { contactListId } = useParams();

  const initialState = {
    name: "",
    number: "",
    email: "",
  };

  const [contact, setContact] = useState(initialState);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      if (!contactId) return;

      try {
        const { data } = await api.get(`/contact-list-items/${contactId}`);
        if (isMounted.current) {
          setContact(data);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setContact(initialState);
  };

  const handleSaveContact = async (values) => {
    try {
      if (contactId) {
        await api.put(`/contact-list-items/${contactId}`, {
          ...values,
          companyId,
          contactListId,
        });
        handleClose();
      } else {
        const { data } = await api.post("/contact-list-items", {
          ...values,
          companyId,
          contactListId,
        });
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("contactModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
        <DialogTitle id="form-dialog-title">
          {contactId
            ? `${i18n.t("contactModal.title.edit")}`
            : `${i18n.t("contactModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={ContactSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveContact(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {(formikProps) => (
            <ContactFormFields
              {...formikProps}
              classes={classes}
              contactId={contactId}
              handleClose={handleClose}
            />
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ContactListItemModal;
